import { useState, useEffect, useCallback } from 'react';
import { Card, Form, Button, Alert, Spinner, Row, Col } from 'react-bootstrap';
import settingsService from '@/shared/services/settingsService';
import { PROGRAM_DISPLAY_ORDER } from '@/shared/utils/programOrder';

const ERROR_ID = 'student-employment-rules-error';

const FIELDS = [
    {
        path: 'weeklyHoursLimit',
        label: 'Weekly hours limit, all campus jobs',
        step: 0.5,
        min: 0.5,
        max: 40,
    },
    {
        path: 'internationalWeeklyHoursLimit',
        label: 'Weekly hours limit, international students',
        step: 0.5,
        min: 0.5,
        max: 40,
    },
    { path: 'minimumAge', label: 'Minimum age', step: 1, min: 1, max: 99 },
    {
        path: 'minimumCreditHours',
        label: 'Minimum credit hours',
        step: 1,
        min: 1,
        max: 30,
    },
    {
        path: 'internationalMinimumCreditHours.undergraduate',
        label: 'Minimum credit hours, international undergraduate',
        step: 1,
        min: 1,
        max: 30,
    },
    {
        path: 'internationalMinimumCreditHours.graduate',
        label: 'Minimum credit hours, international graduate',
        step: 1,
        min: 1,
        max: 30,
    },
    {
        path: 'minimumSemesterGpa',
        label: 'Minimum semester GPA',
        step: 0.1,
        min: 0,
        max: 4,
    },
];

const readPath = (rules, path) =>
    path.split('.').reduce((value, key) => value?.[key], rules);

const toInput = (value) =>
    value === null || value === undefined ? '' : String(value);

// The form holds each input's text under its dotted path.
const toForm = (rules) =>
    Object.fromEntries(
        FIELDS.map(({ path }) => [path, toInput(readPath(rules, path))])
    );

// An empty input is sent as null so the server's refusal names it.
const toNumber = (text) => (text === '' ? null : Number(text));

const toPayload = (form) => ({
    weeklyHoursLimit: toNumber(form.weeklyHoursLimit),
    internationalWeeklyHoursLimit: toNumber(form.internationalWeeklyHoursLimit),
    minimumAge: toNumber(form.minimumAge),
    minimumCreditHours: toNumber(form.minimumCreditHours),
    internationalMinimumCreditHours: {
        undergraduate: toNumber(
            form['internationalMinimumCreditHours.undergraduate']
        ),
        graduate: toNumber(form['internationalMinimumCreditHours.graduate']),
    },
    minimumSemesterGpa: toNumber(form.minimumSemesterGpa),
});

function describeLimits(limits) {
    const hours = limits?.hoursPerWeekPerPosition;
    const positions = limits?.maxPositionsPerStudent;
    const hoursText =
        hours === null || hours === undefined
            ? 'hours per position not set'
            : `${hours} hours a week for one position`;
    const positionsText =
        positions === null || positions === undefined
            ? 'no limit on positions'
            : `up to ${positions} ${
                  positions === 1 ? 'position' : 'positions'
              } per student`;
    return `${hoursText}; ${positionsText}`;
}

function StudentEmploymentRulesCard() {
    const [loadState, setLoadState] = useState('loading');
    const [programLimits, setProgramLimits] = useState({});
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [saveError, setSaveError] = useState(null);

    const load = useCallback(async () => {
        setLoadState('loading');
        try {
            const data = await settingsService.getStudentEmploymentRules();
            setForm(toForm(data));
            setProgramLimits(data.programLimits || {});
            setLoadState('ready');
        } catch {
            setLoadState('error');
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    // The region is mounted empty and given its text afterwards, which is
    // what makes a screen reader announce it
    const [loadingAnnouncement, setLoadingAnnouncement] = useState('');
    useEffect(() => {
        setLoadingAnnouncement(
            loadState === 'loading' ? 'Loading student employment rules...' : ''
        );
    }, [loadState]);

    const handleChange = (path, value) => {
        setSaved(false);
        setForm((prev) => ({ ...prev, [path]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setSaved(false);
        setSaveError(null);
        try {
            const data = await settingsService.updateStudentEmploymentRules(
                toPayload(form)
            );
            setForm(toForm(data));
            setProgramLimits(data.programLimits || {});
            setSaved(true);
        } catch (err) {
            const body = err.response?.data;
            setSaveError(
                err.response?.status === 400 && body?.message
                    ? { message: body.message, field: body.field }
                    : {
                          message:
                              "Couldn't save the student employment rules. Please try again.",
                      }
            );
        } finally {
            setSaving(false);
        }
    };

    let body;
    if (loadState === 'loading') {
        body = (
            <div className="text-center py-4">
                <Spinner animation="border" size="sm" aria-hidden="true" />
            </div>
        );
    } else if (loadState === 'error') {
        body = (
            <Alert variant="danger" role="alert">
                <p className="mb-2">
                    Couldn&apos;t load the student employment rules. Please try
                    again.
                </p>
                <Button variant="outline-danger" size="sm" onClick={load}>
                    Try again
                </Button>
            </Alert>
        );
    } else {
        body = (
            <>
                <Form onSubmit={handleSubmit} noValidate>
                    <Row>
                        {FIELDS.map((field) => {
                            const invalid = saveError?.field === field.path;
                            return (
                                <Col md={6} key={field.path}>
                                    <Form.Group
                                        className="mb-3"
                                        controlId={`student-employment-${field.path}`}
                                    >
                                        <Form.Label>{field.label}</Form.Label>
                                        <Form.Control
                                            type="number"
                                            step={field.step}
                                            min={field.min}
                                            max={field.max}
                                            value={form[field.path]}
                                            aria-invalid={
                                                invalid ? 'true' : undefined
                                            }
                                            aria-describedby={
                                                invalid ? ERROR_ID : undefined
                                            }
                                            onChange={(e) =>
                                                handleChange(
                                                    field.path,
                                                    e.target.value
                                                )
                                            }
                                        />
                                    </Form.Group>
                                </Col>
                            );
                        })}
                    </Row>
                    {saveError && (
                        <Alert variant="danger" role="alert" id={ERROR_ID}>
                            {saveError.message}
                        </Alert>
                    )}
                    <Button type="submit" variant="primary" disabled={saving}>
                        {saving ? 'Saving...' : 'Save rules'}
                    </Button>
                    <p role="status" className="text-success mt-3 mb-0">
                        {saved ? 'Student employment rules saved.' : ''}
                    </p>
                </Form>

                <hr />
                <h3 className="h6">Program limits in effect</h3>
                <ul className="mb-2">
                    {PROGRAM_DISPLAY_ORDER.map((type) => (
                        <li key={type}>
                            {type.toUpperCase()}:{' '}
                            {describeLimits(programLimits[type])}
                        </li>
                    ))}
                </ul>
                <p className="small text-muted mb-0">
                    Change these under Program Config.
                </p>
            </>
        );
    }

    return (
        <Card className="border-0 shadow-sm mb-4">
            <Card.Body>
                <h2 className="h5">Student employment rules</h2>
                <p className="text-muted">
                    These university-wide rules are shown to students on the
                    student application and recorded with their answers. Nothing
                    is checked against them yet: the check when a student
                    accepts a position comes later. Each program sets its own
                    hours per position and positions per student under Program
                    Config.
                </p>
                <p role="status" className="visually-hidden">
                    {loadingAnnouncement}
                </p>
                {body}
            </Card.Body>
        </Card>
    );
}

export default StudentEmploymentRulesCard;
